import logger from '../../Utils/logger';
import React, { useEffect, useRef, useState } from 'react';
import styles from './Style.module.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../Utils/getErrorMessage.js';
import { useCheckUser, useGetCountries } from '../../APICalling/useApiQueries';
import { useCreateBookingRequest } from '../../Utils/Hooks/useCreateBookingRequest';
import TermsAndConditions from '../../Components/TermsAndConditions/TermsAndConditions';
import ToggleSwitch from '../../Components/ToggleSwitch/ToggleSwitch';
import LoaderMain from '../../Components/Loader/LoaderMain.js';
import { Footer } from '../../Components/Cart/Footer.js';
import { useSticky } from '../../Utils/Helper.js';
import { Header } from '../../Components/Header/Header.js';
import Timer from '../../Components/Timer/Timer';
import { SessionExpiredPopup } from '../../Components/Popup/SessionExpiredPopup/SessionExpiredPopup';
import { BlockedCustomerPopup } from '../../Components/Popup/BlockedCustomerPopup/BlockedCustomerPopup';
import { AppointmentSummary } from '../../Components/AppointmentSummary/AppointmentSummary.js';
import Input from '../../Components/FormInputs/Input.js';
import NotesInput from '../../Components/FormInputs/NotesInput.js';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useCustomer } from '../../Context/Customer/CustomerContext';
import { useBookingSession } from '../../Context/BookingSession/BookingSessionContext';
import { hasActiveBookingHold, releaseBookingHoldOnce } from '../../Utils/bookingHold';
import { useHoldExpiry } from '../../Utils/Hooks/useHoldExpiry';
import { useReleaseBookingHold } from '../../Utils/Hooks/useReleaseBookingHold';
import { useBookingTimer } from '../../Context/Timer/useBookingTimer';
import { getSessionStorageValue, removeSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { useWebSettings } from '../../Context/WebSettings/WebSettingsContext.js';
import { Snakbar } from '../../Components/Snakbar/Snakbar.js';
import { isMobileVerificationValid, normalizeMobileNumber } from '../../Utils/bookingVerification.js';
import { getCountryIdByIsoCode } from '../../Utils/countries.js';
import { navigateToBookedAppointment } from '../../Utils/navigateToBookedAppointment';
import { isBlockedCustomerResponse } from '../../Utils/blockedCustomer';

const BOOKING_NOT_FOUND_MESSAGE = "We couldn’t find your booking. We’ll take you back to Review & Confirm in a few seconds so you can try again.";
const BOOKING_REDIRECT_DELAY_MS = 4000;

const getFieldError = (name, value) => {
  const isValidName = /^[\p{L}\d\s]*$/u.test(value);

  if (name === "fname") {
    if (!value.trim()) return "First Name is required";
    if (!isValidName)
      return "First Name can contain only letters, numbers and spaces";
  }

  if (name === "lname") {
    // Last name is optional; only validate format when provided.
    if (value.trim() && !isValidName)
      return "Last Name can contain only letters, numbers and spaces";
  }

  if (name === "email") {
    if (!value?.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value))
      return "Invalid email format";
  }

  if (name === "mobile") {
    if (!value) return "Mobile number is required";
    if (!isValidPhoneNumber(value)) return "Invalid phone number";
    const parsedNumber = parsePhoneNumberFromString(value);
    if (parsedNumber?.country === "SG" && parsedNumber.nationalNumber.length !== 8) {
      return "Invalid phone number";
    }
  }

  return "";
};

export const ContactDetail = () => {
  const { data: countryDirectory } = useGetCountries();
  const { deposit } = useWebSettings();
  const { appointmentTypeID } = useAppointment();
  const {
    formData,
    setFormData,
    isUserVerified,
    setIsUserVerified,
    verifiedMobile,
    setVerifiedMobile,
    setUserID,
  } = useCustomer();
  const { setBookingID, setUuid, uuid } = useBookingSession();
  const { isTimesup, handleSessionExpiredClose } = useHoldExpiry();
  const { releaseHold } = useReleaseBookingHold();
  const { startTimer, resetTimer } = useBookingTimer();
  const [mainLoading, setMainLoading] = useState(true);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const currentMobile = normalizeMobileNumber(formData.mobile);
  const [blockedMobile, setBlockedMobile] = useState('');
  const isCustomerBlocked = Boolean(currentMobile && blockedMobile === currentMobile);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isAgreeTerms = true;
  const [isSubscribed, setIsSubscribed] = useState(1);
  const [isDepositAcknowledged, setIsDepositAcknowledged] = useState(true);
  const [notesError, setNotesError] = useState("");
  const { isSticky, sentinelRef } = useSticky();
  const fnameRef = useRef(null);
  const lnameRef = useRef(null);
  const mobileRef = useRef(null);
  const emailRef = useRef(null);
  const termsRef = useRef(null);
  const depositAckRef = useRef(null);

  // Holds the customer payload between booking creation and user linking steps.
  const customerPayloadRef = useRef(null);
  // Holds the booking UUID returned from /bookings/create until user linking completes.
  const pendingUuidRef = useRef(null);

  const handleBlockedCustomer = () => {
    setBlockedMobile(currentMobile);
    setIsRedirecting(false);
    setErrors((previous) => ({ ...previous, error: '' }));
    pendingUuidRef.current = null;
    resetTimer();
    // Keep the booking ID until the user leaves, so the route guard does not
    // redirect away from the blocked-account popup.
    const holdUuid = uuid || getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID);
    if (holdUuid) {
      releaseBookingHoldOnce(holdUuid).catch((error) => {
        logger.error('Error releasing blocked customer booking hold:', error);
      });
    }
    setUuid(null);
  };

  const handleBookingError = (payload) => {
    if (isBlockedCustomerResponse(payload)) {
      handleBlockedCustomer();
      return true;
    }

    const message = getErrorMessage(payload?.response?.data ?? payload);
    const isBookingNotFound = /\bbooking\s+not\s+found\b/i.test(message);

    if (isBookingNotFound) {
      // Keep this page mounted long enough to read the notice before clearing
      // the booking ID, which would otherwise trigger the route guard at once.
      resetTimer();
      setIsRedirecting(true);
    }

    setErrors((prev) => ({
      ...prev,
      error: isBookingNotFound ? BOOKING_NOT_FOUND_MESSAGE : message,
    }));
    return isBookingNotFound;
  };

  const checkUserMutation = useCheckUser({
    onSuccess: (response) => {
      if (isBlockedCustomerResponse(response)) {
        handleBlockedCustomer();
        return;
      }
      if (response?.data?.success) {
        const userId = response.data.data.userId;
        setUserID(userId);
        resetTimer();
        setUuid(null);
        navigateToBookedAppointment(navigate, pendingUuidRef.current);
      } else {
        handleBookingError(response);
      }
    },
    onError: (error) => {
  if (isBlockedCustomerResponse(error)) {
    handleBlockedCustomer();
    return;
  }

  const bookingError = error?.response?.data ?? error;

  if (handleBookingError(bookingError)) {
    return;
  }

  // Booking was already created — clean up so the user can retry.
  releaseHold();
  pendingUuidRef.current = null;
},
  });

  const { createBooking, isPending: isCreateBookingPending } = useCreateBookingRequest({
    onSuccess: (response) => {
      if (response?.data?.success) {
        const bookingId = response?.data?.data?.booking?.id || null;
        const uuid = response?.data?.data?.booking?.uuid || null;
        setBookingID(bookingId);
        if (uuid) setUuid(uuid);
        pendingUuidRef.current = uuid;
        checkUserMutation.mutate({ ...customerPayloadRef.current, uuid });
      } else {
        handleBookingError(response);
      }
    },
    onError: (error) => {
      handleBookingError(error);
    },
  });

  const hasVerifiedMobile = isMobileVerificationValid({
    isUserVerified,
    verifiedMobile,
    currentMobile: formData.mobile,
  });

  const isSubmitDisabled =
    isRedirecting ||
    !hasVerifiedMobile ||
    !isDepositAcknowledged ||
    checkUserMutation.isPending ||
    isCreateBookingPending ||
    isTimesup ||
    isCustomerBlocked;

  const subscribedNewsletter = (checked) => setIsSubscribed(Number(checked));

  const scrollToErrorField = (fieldName) => {
    const fieldMap = {
      fname: fnameRef,
      lname: lnameRef,
      mobile: mobileRef,
      email: emailRef,
      agreeTerms: termsRef,
    };

    const targetRef = fieldMap[fieldName];
    if (!targetRef?.current) return;

    targetRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusableEl = targetRef.current.querySelector("input, textarea, select");
    focusableEl?.focus();
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    if (value.length > 250) {
      setFormData((prev) => ({ ...prev, notes: value.slice(0, 250) }));
      setNotesError("Maximum 250 characters allowed.");
      return;
    }
    setFormData((prev) => ({ ...prev, notes: value }));
    setNotesError("");
  };

  const validateField = (name, value) => {
    const error = getFieldError(name, value);

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
      error: "",
    }));

    return error === "";
  };

  const handleNameChange = (e) => {
    const { name, value } = e.target;
    if (!/^[\p{L}\d\s]*$/u.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, mobile: value }));
    validateField("mobile", value);
    setIsUserVerified(false);
    setVerifiedMobile("");
  };

  // const handleCountryChange = (country) => {
  //   if (!country) return;
  //   setFormData((prev) => ({
  //     ...prev,
  //     countryId: getCountryIdByIsoCode(country) || prev.countryId || "",
  //   }));
  // };

  const validateForm = () => {
    const fnameValid = validateField("fname", formData.fname);
    const lnameValid = validateField("lname", formData.lname);
    const mobileValid = validateField("mobile", formData.mobile);
    const emailValid = validateField("email", formData.email);

    if (!isAgreeTerms) {
      setErrors((prev) => ({
        ...prev,
        agreeTerms: "You must agree to the Terms and Conditions",
      }));
      return { isValid: false, firstInvalidField: "agreeTerms" };
    }

    setErrors((prev) => ({ ...prev, agreeTerms: "" }));

    const firstInvalidField =
      (!fnameValid && "fname") ||
      (!lnameValid && "lname") ||
      (!mobileValid && "mobile") ||
      (!emailValid && "email") ||
      null;

    return {
      isValid:
        fnameValid && lnameValid && mobileValid && emailValid && isAgreeTerms,
      firstInvalidField,
    };
  };

  const handleCreateBooking = () => {
    // Guard against duplicate hold creation (e.g. double-submit race).
    if (hasActiveBookingHold()) {
      const sessionUuid = getSessionStorageValue(STORAGE_KEYS.BOOKING_UUID);
      if (sessionUuid) {
        pendingUuidRef.current = sessionUuid;
        checkUserMutation.mutate({ ...customerPayloadRef.current, uuid: sessionUuid });
        return;
      }
    }

    // A timer and stale booking ID alone do not identify a usable hold.
    createBooking({});
  };

  
  const getDisabledButtonMessage = () => {
    if (isRedirecting) return BOOKING_NOT_FOUND_MESSAGE;
    if (isTimesup) return "Your session has expired. Please refresh and try again.";
    if (checkUserMutation.isPending || isCreateBookingPending) return "Please wait while your booking is being processed...";
    if (!hasVerifiedMobile) return "Please verify your mobile number before continuing.";
    if (!isDepositAcknowledged) return "Please acknowledge the deposit amount to proceed with your booking.";
    return "";
  };

  const handleButtonClick = (e) => {
    e?.preventDefault?.();
    if (isSubmitDisabled) {
      const disabledMessage = getDisabledButtonMessage();
      if (disabledMessage) {
        setErrors((prev) => ({ ...prev, error: disabledMessage }));

        // Scroll to deposit acknowledgment toggle if that's the issue
        if (!isDepositAcknowledged && depositAckRef?.current) {
          depositAckRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          depositAckRef.current.focus();
        }
      }
      return;
    }
    handleSubmit(e);
  };

const handleSubmit = (e) => {
  e?.preventDefault?.();

  if (isCustomerBlocked || isRedirecting) return;

  if (!hasVerifiedMobile) {
    navigate("/review-and-confirm-booking", { replace: true });
    return;
  }

  const { isValid, firstInvalidField } = validateForm();

  if (!isValid) {
    if (firstInvalidField) {
      scrollToErrorField(firstInvalidField);
    }
    return;
  }

  setErrors((prev) => ({ ...prev, error: "" }));

  const phoneNumber = parsePhoneNumberFromString(formData.mobile || "");
  const countryId =
    getCountryIdByIsoCode(
      phoneNumber?.country,
      countryDirectory?.byCode
    ) || formData.countryId;

  const customerPayload = {
    customer_name: formData.fname,
    customer_lastname: formData.lname,
    customer_email: formData.email,
    mobile_number: formData.mobile,
    country: countryId,
    panel: "customer",
    newsletter: isSubscribed,
    web_notes: formData.notes ?? "",
  };

  if (uuid) {
    // Booking was created during OTP verification — link the customer.
    pendingUuidRef.current = uuid;
    checkUserMutation.mutate({
      ...customerPayload,
      uuid,
    });
    return;
  }

  // Fallback when booking creation was skipped or failed during OTP verification.
  customerPayloadRef.current = customerPayload;
  handleCreateBooking();
};

  useEffect(() => {
    const selectedDate = getSessionStorageValue(STORAGE_KEYS.DATE);
    const selectedTime = getSessionStorageValue(STORAGE_KEYS.TIME);
    const selectedOutletID = getSessionStorageValue(STORAGE_KEYS.SELECTED_OUTLET_ID);

    if (!selectedDate || !selectedTime || !selectedOutletID || appointmentTypeID == null) {
      navigate('/');
    }
  }, [appointmentTypeID, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMainLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Discard flags saved by older versions; only a new API error can block.
    removeSessionStorageValue(STORAGE_KEYS.BLOCKED_CUSTOMER_MOBILE);
    setBlockedMobile('');
  }, []);

  useEffect(() => {
    if (blockedMobile && blockedMobile !== currentMobile) {
      setBlockedMobile('');
    }
  }, [blockedMobile, currentMobile]);

  // Stop the countdown only after the current request reports a blocked account.
  useEffect(() => {
    if (isCustomerBlocked) resetTimer();
    else startTimer();
  }, [isCustomerBlocked, startTimer, resetTimer]);

  useEffect(() => {
    if (errors.error) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [errors.error]);

  useEffect(() => {
    if (!isRedirecting || isCustomerBlocked) return;

    const timer = setTimeout(() => {
      pendingUuidRef.current = null;
      setUuid(null);
      setBookingID(null);
      navigate('/review-and-confirm-booking', { replace: true });
    }, BOOKING_REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isRedirecting, isCustomerBlocked, navigate, setBookingID, setUuid]);


  useEffect(() => {
    if (isTimesup || isCustomerBlocked) {
      document.body.classList.add('overlay', 'overflow-hidden', 'overlay-index');
    } else {
      document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
    }
    if (isTimesup) {
      setFormData({
        fname: '',
        lname: '',
        email: '',
        mobile: '',
        countryId: '',
        notes: '',
      });
      setIsUserVerified(false);
    }
    return () => {
      document.body.classList.remove('overlay', 'overflow-hidden', 'overlay-index');
    };
  }, [isTimesup, isCustomerBlocked, setFormData, setIsUserVerified]);

  useEffect(() => {
    if (isDepositAcknowledged) {
      setErrors((prev) => ({ ...prev, error: "" }));
    }
  }, [isDepositAcknowledged]);

  if (mainLoading && !isCustomerBlocked) return <LoaderMain />;
  logger.log("ContactDetail render: isTimesup =", formData, isTimesup, "isSubmitDisabled =", isSubmitDisabled);

  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
      <Timer customClass={styles.timer} />
      <Header
        title="Contact Person"
        onBack={() => navigate("/review-and-confirm-booking")}
      />
    </div>
    
    <div className={`${styles.ContactDetailPage} space-y`}>
      <div className={styles.userDetailCard}>
        <form onSubmit={handleSubmit}>
        <div className={`${styles.ContactDetail} space-x container-md`}>
          <div className={styles.inputGroup}>
            {/* First Name */}
            <Input
              inputRef={fnameRef}
              label="First Name"
              required
              name="fname"
              value={formData.fname}
              onChange={handleNameChange}
              error={errors.fname}
              wrapperClassName={styles.formGroup}
              labelClassName={styles.formLabel}
            />

            {/* Last Name */}
            <Input
              inputRef={lnameRef}
              label="Last Name"
              name="lname"
              value={formData.lname}
              onChange={handleNameChange}
              error={errors.lname}
              wrapperClassName={styles.formGroup}
              labelClassName={styles.formLabel}
            />
          </div>

          {/* Phone */}
          {/* <Input
            inputRef={mobileRef}
            label="Mobile Number"
            required
            name="mobile"
            isPhone
            placeholder="Enter phone number"
            defaultCountry="SG"
            international={false}
            value={formData.mobile}
            onChange={handlePhoneChange}
            onCountryChange={handleCountryChange}
            error={errors.mobile}
            disabled={hasVerifiedMobile}
            wrapperClassName={styles.formGroup}
            labelClassName={styles.formLabel}
            subText={hasVerifiedMobile ? "Verified for this booking." : ""}
          /> */}

          {/* Email */}
          <Input
            inputRef={emailRef}
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleEmailChange}
            error={errors.email}
            required
            wrapperClassName={styles.formGroup}
            labelClassName={styles.formLabel}
            subText="Please enter the correct email so we can send your booking confirmation."
          />

          <div className={styles.hr} />

          {/* Notes */}
          <NotesInput
            name="notes"
            label="Notes"
            optionalText="(Optional)"
            value={formData.notes}
            onChange={handleNotesChange}
            error={notesError}
            subText="Basic requests only (e.g. pressure or allergies). We’ll try our best but may not be able to accommodate all requests."
            maxLength={250}
            wrapperClassName={styles.notes}
            formGroupClassName={styles.formGroup}
            labelClassName={styles.formLabel}
            countClassName={styles.notesLength}
          />

          {/* Newsletter */}
          <div className={styles.termsAndConditions}>
            <div className={styles.termsAndConditionsInner}>
              <p>Stay up to date with Natureland’s products, services and offers.
                {/* <span className={styles.red}>*</span> */}
              </p>
              <ToggleSwitch
                name="subscribe"
                checked={isSubscribed}
                ariaLabel="Subscribe to Natureland updates and offers"
                handleChange={subscribedNewsletter}
              />
            </div>
          </div>

          {/* Non-refundable deposit acknowledgement */}
          <div className={styles.termsAndConditions} ref={depositAckRef} tabIndex={-1}>
            {/* <div className={styles.termsAndConditionsInner}> */}
              {/* <p style={{ color: !isDepositAcknowledged ? '#e74c3c' : 'inherit', fontWeight: !isDepositAcknowledged ? '600' : '400' }}>
                I acknowledge that the booking deposit is non-refundable.<span className={styles.red}>*</span>
              </p> */}
              {/* <ToggleSwitch
                name="depositAcknowledgement"
                checked={isDepositAcknowledged}
                ariaLabel="Acknowledge that the booking deposit is non-refundable"
                handleChange={setIsDepositAcknowledged}
              /> */}
            {/* </div> */}
          </div>
        </div>
          <div className={styles.hr} />
          <div className="container-md space-x">
          <AppointmentSummary
          noBookingID={true}
          isDepositPaid={false}
          />
          </div>
          <div className={`${styles.hr} my-0`} />

          <div ref={termsRef}>
            <TermsAndConditions />
          </div>

          <Snakbar
          text={isRedirecting ? BOOKING_NOT_FOUND_MESSAGE : errors.error}
          active={isRedirecting || errors.error}
          varient="danger"
          type="error"
          hasButton={!isRedirecting}
          onClose={() => setErrors((prev) => ({ ...prev, error: "" }))}
          />
          
          <Footer
            btnLabel={
              checkUserMutation.isPending || isCreateBookingPending
                ? "Processing..." 
                : `Confirm Booking`
                // : `Book and Pay Deposit ($${deposit})`
            }
            btnClick={handleButtonClick}
            isFooterActive="true"
            disabled={isSubmitDisabled}
            customClass={styles.footer}
            customClassForTerms={styles.terms}
          />

        </form>
      </div>
    </div>
    {isTimesup && (
      <SessionExpiredPopup
        active={isTimesup}
        onClose={handleSessionExpiredClose}
      />
    )}
    <BlockedCustomerPopup
      active={isCustomerBlocked}
      onReturnHome={() => navigate('/', { replace: true })}
      // No need to show the close button since the user cannot continue booking.
    />
    </>
  );
};
